import { useEffect, useMemo, useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Images, Loader2, Truck, Boxes, Archive, ChevronUp, ChevronDown, Tag, ArrowLeft, Search, CheckCircle2, Link2, Clock, AlertTriangle, User , Maximize2} from "lucide-react";
import { SearchCameraButton } from "@/components/search/engine";
import { useOwnTag } from "@/components/search/SearchBox";

// Compact capture-time label for a pool photo (device wall-clock, EXIF DateTimeOriginal).
const capTime = (p) => {
  const d = p?.captured_at_display || p?.captured_at;
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(String(d));
  return m ? `${m[2]}/${m[3]} ${m[4]}:${m[5]}:${m[6]}` : String(d);
};
const capRange = (photos) => {
  const times = photos.map(capTime).filter(Boolean);
  if (!times.length) return null;
  return times.length === 1 ? times[0] : `${times[0]} – ${times[times.length - 1]}`;
};

const STATUS_RANK = { dismantling: 0, partially_dismantled: 1, fully_dismantled: 2, whole: 3, awaiting: 4, open: 5, closed: 9 };
const statusStyle = (s) => ({
  dismantling: "bg-[#E0781A] text-white", partially_dismantled: "bg-amber-500 text-white",
  fully_dismantled: "bg-amber-700 text-white", whole: "bg-slate-500 text-white", awaiting: "bg-slate-400 text-white",
  open: "bg-emerald-600 text-white", closed: "bg-slate-300 text-slate-700",
}[s] || "bg-slate-400 text-white");

const TypeCell = ({ g }) => {
  if (g.group_type === "donor") return <span className="inline-flex items-center gap-1.5 text-slate-600"><Truck className="w-4 h-4 text-[#B01E28]" />Donor truck</span>;
  if (g.kind === "legacy") return <span className="inline-flex items-center gap-1.5 text-[#5E3A93]"><Archive className="w-4 h-4" />Legacy lot</span>;
  return <span className="inline-flex items-center gap-1.5 text-[#C76714]"><Boxes className="w-4 h-4" />Standard lot</span>;
};

export default function TagToPhotosPage() {
  const [groups, setGroups] = useState([]);
  const [shot, setShot] = useState(null);   // the one photo viewer
  const [loading, setLoading] = useState(true);
  const [includeDone, setIncludeDone] = useBackState("includeDone", false);
  const [sort, setSort] = useBackState("sort", { key: "unmatched", dir: "desc" });
  const [search, setSearch] = useBackState("search", "");
  const [asked, setAsked] = useState("");   // what the server last searched — S4: the box is on the one engine
  useEffect(() => {
    const t = setTimeout(() => setAsked(search.trim()), search.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]);

  const [selected, setSelected] = useState(null);
  const [pool, setPool] = useState([]);          // [{file_id, seq, assigned, is_tag_photo}]
  const [tags, setTags] = useState([]);          // [{id, short_code, name, photo_count, tag_photo_file}]
  const [wsLoading, setWsLoading] = useState(false);
  const [activeTag, setActiveTag] = useState(null);   // part id selected on the right
  const [picked, setPicked] = useState([]);           // file_ids selected on the left (ordered)
  const [attaching, setAttaching] = useState(false);

  const load = async (done) => {
    setLoading(true);
    try { const { data } = await api.get("/tag-to-photos/groups", { params: { include_done: done ? "true" : "false", q: asked } }); setGroups(data.groups || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(includeDone); }, [includeDone, asked]);
  // ENTER AND SCANNING (S4, his rule): one of our part tags opens the part, a truck's own stock # opens that truck's photos here; anything else is a list.
  const ownTag = useOwnTag("ttp-search");
  const navigate = useNavigate();
  const location = useLocation();
  const onEnter = async (v) => {
    const t = (v || "").trim();
    if (!t) return;
    setSearch(t); setAsked(t);
    const hit = await ownTag(t);
    if (hit?.kind === "part") { navigate(`/parts/${hit.id}`, { state: fromState(location, "Tag-to-Photos") }); return; }
    if (hit?.kind !== "truck") return;
    const { data } = await api.get("/tag-to-photos/groups", { params: { include_done: includeDone ? "true" : "false", q: hit.label } })
      .catch(() => ({ data: {} }));
    const g = (data.groups || []).find((x) => x.group_type === "donor" && x.group_id === hit.id);
    if (g) openGroup(g);
  };

  const loadWorkspace = async (g) => {
    setWsLoading(true); setActiveTag(null); setPicked([]);
    try {
      const [p, t] = await Promise.all([
        api.get(`/groups/${g.group_type}/${g.group_id}/photos`),
        api.get(`/tag-to-photos/groups/${g.group_type}/${g.group_id}/tags`),
      ]);
      setPool((p.data.photos || []).filter((x) => !x.is_archived));
      setTags(t.data.tags || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWsLoading(false); }
  };

  const openGroup = (g) => { setSelected(g); setPool([]); setTags([]); loadWorkspace(g); };
  const backToList = () => { setSelected(null); setPool([]); setTags([]); setActiveTag(null); setPicked([]); load(includeDone); };

  const unmatched = useMemo(() => pool.filter((p) => !p.assigned), [pool]);
  const togglePick = (fid) => setPicked((cur) => cur.includes(fid) ? cur.filter((x) => x !== fid) : [...cur, fid]);

  const attach = async () => {
    if (!activeTag || picked.length === 0) return;
    setAttaching(true);
    try {
      const { data } = await api.post(`/parts/${activeTag}/photos/from-pool`, { file_ids: picked, group_type: selected.group_type, group_id: selected.group_id });
      toast.success(`Attached ${data.matched} photo${data.matched === 1 ? "" : "s"}${data.tag_photo ? " — tag photo locked" : ""}`);
      setPicked([]); setActiveTag(null);
      loadWorkspace(selected);   // refresh pool + tags (matched drop out)
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAttaching(false); }
  };

  // ONE-TAP: attach an entire part-group (already tag-first, in order) to the active tag.
  // Fewer manual picks = fewer mis-match chances, right where errors happen.
  const attachGroup = async (photos) => {
    if (!activeTag) { toast.error("Pick the tag on the right first, then tap 'Attach whole part'."); return; }
    if (!photos.length) return;
    setAttaching(true);
    try {
      const fileIds = photos.map((p) => p.file_id);
      const { data } = await api.post(`/parts/${activeTag}/photos/from-pool`, { file_ids: fileIds, group_type: selected.group_type, group_id: selected.group_id });
      toast.success(`Attached whole part — ${data.matched} photo${data.matched === 1 ? "" : "s"}${data.tag_photo ? " · tag photo locked" : ""}`);
      setPicked([]); setActiveTag(null);
      loadWorkspace(selected);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAttaching(false); }
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const visible = useMemo(() => {
    let arr = groups;
    const val = (g) => ({
      group: `${g.label} ${g.sub_label}`.toLowerCase(), type: `${g.group_type}-${g.kind || ""}`,
      status: STATUS_RANK[g.status] ?? 6, unmatched: g.pool_unmatched, pool: g.pool_total,
      progress: g.tags_total ? g.tags_matched / g.tags_total : 0, last: g.last_activity || "",
    }[sort.key]);
    arr = [...arr].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0));
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [groups, sort]);

  const SortHead = ({ k, label, className = "" }) => (
    <th data-testid={`ttp-sort-${k}`} onClick={() => toggleSort(k)} className={`px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-slate-500 cursor-pointer select-none hover:text-slate-800 ${className}`}>
      <span className="inline-flex items-center gap-1">{label}{sort.key === k && (sort.dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}</span>
    </th>
  );

  // ---------- MATCHING WORKSPACE ----------
  if (selected) {
    // Group the pool by capture SESSION (one dump run / one uploader). The backend
    // already returns photos session-contiguous + chronological; we render a divider
    // per session so two employees' clusters are visibly separate and never confused.
    const sessionGroups = [];
    unmatched.forEach((p) => {
      const key = `${p.session_id || "__legacy__"}|${p.part_group ?? 0}`;
      const last = sessionGroups[sessionGroups.length - 1];
      if (last && last.key === key) last.photos.push(p);
      else sessionGroups.push({ key, uploader: p.uploader, part_group: p.part_group ?? 0, photos: [p] });
    });
    const Thumb = (p) => {
      const on = picked.includes(p.file_id);
      return (
        <button key={p.file_id} data-testid={`ttp-pool-${p.file_id}`} onClick={() => togglePick(p.file_id)}
          className={`relative rounded-lg overflow-hidden border-2 transition-colors ${on ? "border-[#B01E28] ring-2 ring-[#B01E28]/30" : "border-slate-200 hover:border-slate-400"}`}>
          <AuthImg fileId={p.file_id} thumb className="w-full aspect-square object-cover bg-slate-100" />
          {p.no_capture_time
            ? <Badge data-testid={`ttp-notime-${p.file_id}`} className="absolute bottom-1 left-1 rounded-full border-0 bg-amber-500 text-white text-[9px] font-mono px-1"><AlertTriangle className="w-2.5 h-2.5" /></Badge>
            : <span data-testid={`ttp-time-${p.file_id}`} className="absolute bottom-0 inset-x-0 bg-slate-900/70 text-white text-[8px] font-mono text-center py-0.5">{capTime(p)}</span>}
          {on && <Badge className="absolute top-1 right-1 rounded-full border-0 bg-[#B01E28] text-white text-[10px] font-mono">#{picked.indexOf(p.file_id) + 1}</Badge>}
          <span role="button" tabIndex={0} title="Open this photo" data-testid={`ttp-open-${p.file_id}`}
            onClick={(e) => { e.stopPropagation(); setShot([p]); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setShot([p]); } }}
            className="absolute top-1 left-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 text-slate-700 shadow border border-slate-200">
            <Maximize2 className="w-3.5 h-3.5" />
          </span>
        </button>
      );
    };
    return (
      <div data-testid="tag-to-photos-page">
        <button data-testid="ttp-back" onClick={backToList} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="w-4 h-4" />All lots</button>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="font-display text-2xl font-bold tracking-tight"><span className="font-mono">{selected.label}</span></h1>
          <Badge className={`rounded-full border-0 text-[10px] font-mono uppercase ${statusStyle(selected.status)}`}>{selected.status}</Badge>
          {selected.kind === "legacy" && <Badge className="rounded-full border-0 bg-[#F6F1FB] text-[#5E3A93] text-[10px] font-mono">LEGACY · $0</Badge>}
          <Badge data-testid="ttp-progress" className="rounded-full border-0 bg-slate-800 text-white text-[11px] font-mono"><CheckCircle2 className="w-3 h-3 mr-1" />{tags.filter((t) => t.photo_count > 0).length} of {tags.length} tags matched</Badge>
          <Badge data-testid="ttp-unmatched-count" className="rounded-full border-0 bg-[#E0781A] text-white text-[11px] font-mono">{unmatched.length} photos unmatched</Badge>
        </div>
        <p className="text-sm text-slate-400 mb-4">{selected.sub_label}{selected.vin ? <span className="font-mono text-slate-600 font-semibold"> · VIN {selected.vin}</span> : ""} · Read the tag photo, pick the tag on the right, then tap <span className="font-semibold text-[#B01E28]">"Attach part"</span> to match the whole part in one tap (or pick photos individually).</p>

        {wsLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" />Loading lot…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* LEFT: pool pile */}
            <div className="lg:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><Clock className="w-3 h-3" />Photo pool · grouped by capture session · tag photo leads</div>
              {unmatched.length === 0 ? (
                <div data-testid="ttp-pool-empty" className="border border-dashed border-emerald-300 bg-emerald-50/40 rounded-xl py-12 text-center text-emerald-700 text-sm">All photos matched. 🎉</div>
              ) : (
                <>
                  {sessionGroups.map((g, gi) => {
                    const isFirst = gi === 0;
                    const lead = isFirst ? g.photos[0] : null;
                    const rest = isFirst ? g.photos.slice(1) : g.photos;
                    const uploaderShort = (g.uploader || "").split("@")[0] || (g.key.startsWith("__legacy__") ? "earlier upload" : "unknown");
                    const range = capRange(g.photos);
                    return (
                      <div key={g.key + gi} data-testid={`ttp-session-${gi}`} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="h-px flex-1 bg-slate-200" />
                          <span data-testid={`ttp-session-label-${gi}`} className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                            <User className="w-3 h-3" />{uploaderShort}
                            <span className="text-[#B01E28] font-bold">· Part {(g.part_group ?? 0) + 1}</span>
                            {range ? <span className="text-slate-400">· {range}</span> : (g.key.startsWith("__legacy__") ? <span className="text-slate-400">· earlier upload</span> : null)}
                            <span className="text-slate-400">· {g.photos.length} photo{g.photos.length === 1 ? "" : "s"}</span>
                          </span>
                          <Button data-testid={`ttp-attach-part-${gi}`} size="sm" variant="outline" disabled={!activeTag || attaching}
                            onClick={() => attachGroup(g.photos)}
                            className="h-7 rounded-full text-[10px] border-[#B01E28]/40 text-[#B01E28] hover:bg-[#FDF3F4] shrink-0 disabled:opacity-40">
                            <Link2 className="w-3 h-3 mr-1" />Attach part{activeTag ? ` → ${tags.find((t) => t.id === activeTag)?.short_code}` : ""}
                          </Button>
                          <span className="h-px flex-1 bg-slate-200" />
                        </div>
                        {lead && (
                          <div data-testid="ttp-lead-photo" className={`mb-3 rounded-xl overflow-hidden border-2 cursor-pointer transition-colors ${picked.includes(lead.file_id) ? "border-[#B01E28] ring-2 ring-[#B01E28]/30" : "border-[#E0781A]/50"}`} onClick={() => togglePick(lead.file_id)}>
                            <div className="relative">
                              <AuthImg fileId={lead.file_id} className="w-full max-h-80 object-contain bg-slate-900" />
                              <Badge className="absolute top-2 left-2 rounded-full border-0 bg-[#E0781A] text-white text-[10px] font-mono"><Tag className="w-3 h-3 mr-1" />TAG PHOTO · read this</Badge>
                              {lead.no_capture_time
                                ? <Badge data-testid="ttp-lead-notime" className="absolute bottom-2 left-2 rounded-full border-0 bg-amber-500 text-white text-[10px] font-mono"><AlertTriangle className="w-3 h-3 mr-1" />no capture time — verify</Badge>
                                : <Badge data-testid="ttp-lead-time" className="absolute bottom-2 left-2 rounded-full border-0 bg-slate-900/80 text-white text-[10px] font-mono"><Clock className="w-3 h-3 mr-1" />{capTime(lead)}</Badge>}
                              {picked.includes(lead.file_id) && <Badge className="absolute top-2 right-2 rounded-full border-0 bg-[#B01E28] text-white text-[10px] font-mono">#{picked.indexOf(lead.file_id) + 1} picked</Badge>}
                              <span role="button" tabIndex={0} title="Open this photo" data-testid="ttp-lead-open"
                                onClick={(e) => { e.stopPropagation(); setShot([lead]); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setShot([lead]); } }}
                                className="absolute top-2 right-2 mr-24 inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/90 text-slate-700 shadow border border-slate-200">
                                <Maximize2 className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        )}
                        {rest.length > 0 && (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {rest.map((p) => Thumb(p))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* RIGHT: tag list */}
            <div className="lg:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Tags in this lot</div>
              <div className="space-y-2 max-h-[60dvh] overflow-auto pr-1">
                {tags.length === 0 ? (
                  <div className="text-sm text-slate-400 py-6">No tags in this lot.</div>
                ) : tags.map((t) => {
                  const on = activeTag === t.id;
                  const ready = t.photo_count > 0;
                  return (
                    <button key={t.id} data-testid={`ttp-tag-${t.id}`} onClick={() => setActiveTag(on ? null : t.id)}
                      className={`w-full flex items-center gap-3 border rounded-lg px-3 py-2.5 text-left transition-colors ${on ? "border-[#B01E28] bg-[#FFF9F3] ring-2 ring-[#B01E28]/20" : ready ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 hover:border-slate-400"}`}>
                      <Tag className={`w-4 h-4 shrink-0 ${on ? "text-[#B01E28]" : ready ? "text-emerald-600" : "text-slate-400"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-semibold text-slate-800">{t.short_code}</div>
                        <div className="text-xs text-slate-400 truncate">{t.name || "Unidentified part"}</div>
                      </div>
                      {t.tag_photo_file && <AuthImg fileId={t.tag_photo_file} thumb className="w-9 h-9 object-cover rounded border border-slate-200" />}
                      {ready
                        ? <Badge data-testid={`ttp-tag-state-${t.id}`} className="rounded-full border-0 bg-emerald-600 text-white text-[10px] font-mono"><CheckCircle2 className="w-3 h-3 mr-1" />{t.photo_count}</Badge>
                        : <Badge data-testid={`ttp-tag-state-${t.id}`} className="rounded-full border-0 bg-slate-400 text-white text-[10px] font-mono">no photos</Badge>}
                    </button>
                  );
                })}
              </div>

              <div className="sticky bottom-0 mt-3 bg-white pt-2 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-2">
                  {activeTag ? <span><span className="font-mono font-semibold text-slate-700">{tags.find((t) => t.id === activeTag)?.short_code}</span> selected · </span> : "Pick a tag, then "}
                  {picked.length} photo{picked.length === 1 ? "" : "s"} chosen{picked.length > 0 ? " (first = tag photo)" : ""}.
                </div>
                <Button data-testid="ttp-attach-btn" disabled={!activeTag || picked.length === 0 || attaching} onClick={attach}
                  className="w-full h-11 rounded-xl bg-[#B01E28] hover:bg-[#8f1820] text-white font-semibold">
                  {attaching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Attach {picked.length || ""} to this tag
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- LOTS LIST ----------
  return (
    <div data-testid="tag-to-photos-page">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2"><Images className="w-6 h-6 text-[#B01E28]" />Tag-to-Photos</h1>
      <p className="text-sm text-slate-500 mb-3">Match the dumped pool photos to their tags. Open a lot, read the <span className="font-semibold">tag photo</span>, pick its photos, attach to the right tag. Matched photos leave the pile.</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5 w-80 max-w-full">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="ttp-search" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(e.currentTarget.value); } }} enterKeyHint="search"
              placeholder="Find by stock#, VIN, make/model, lot…" className="pl-9 h-10 rounded-lg border-slate-200" />
          </div>
          <SearchCameraButton testid="ttp-search" onScan={onEnter} />
        </div>
        <Badge data-testid="ttp-total-unmatched" className="rounded-full bg-[#E0781A] text-white border-0">{groups.reduce((n, g) => n + g.pool_unmatched, 0)} photos to match</Badge>
        <button data-testid="ttp-toggle-done" onClick={() => setIncludeDone((v) => !v)} className="text-xs font-mono text-slate-500 hover:text-slate-800 underline">
          {includeDone ? "Hide fully-matched lots" : "Show all lots with pool photos"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" />Loading lots…</div>
      ) : visible.length === 0 ? (
        <div data-testid="ttp-empty" className="border border-dashed border-slate-300 rounded-xl py-12 text-center text-slate-400 text-sm">{search ? "No lots match your search." : "No lots have photos waiting to match. 🎉"}</div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <SortHead k="group" label="Lot" /><SortHead k="type" label="Type" /><SortHead k="status" label="Status" />
                <SortHead k="unmatched" label="Unmatched" /><SortHead k="progress" label="Tags matched" /><SortHead k="last" label="Last activity" className="hidden md:table-cell" />
              </tr>
            </thead>
            <tbody>
              {visible.map((g) => (
                <tr key={`${g.group_type}-${g.group_id}`} data-testid={`ttp-row-${g.group_id}`} onClick={() => openGroup(g)} className="border-b border-slate-100 cursor-pointer hover:bg-[#FFF9F3]">
                  <td className="px-3 py-2.5"><div className="font-mono font-semibold text-slate-800">{g.label}</div><div className="text-xs text-slate-400 truncate max-w-[220px]">{g.sub_label}</div>{g.vin ? <div data-testid={`ttp-vin-${g.group_id}`} className="font-mono text-[10px] text-slate-600 font-semibold truncate max-w-[220px]">VIN {g.vin}</div> : null}</td>
                  <td className="px-3 py-2.5 text-xs"><TypeCell g={g} /></td>
                  <td className="px-3 py-2.5"><Badge className={`rounded-full border-0 text-[10px] font-mono uppercase ${statusStyle(g.status)}`}>{g.status}</Badge></td>
                  <td className="px-3 py-2.5"><span data-testid={`ttp-unmatched-${g.group_id}`} className={`font-mono font-bold ${g.pool_unmatched ? "text-[#E0781A]" : "text-slate-300"}`}>{g.pool_unmatched}</span></td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 font-mono">{g.tags_matched}/{g.tags_total} tags</td>
                  <td className="px-3 py-2.5 text-xs text-slate-400 font-mono hidden md:table-cell">{(g.last_activity || "").slice(0, 10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PhotoLightbox photos={shot || []} index={shot ? 0 : null} onIndex={() => {}}
        onClose={() => setShot(null)} testidPrefix="ttp-photo-viewer" />
    </div>
  );
}
